/**
 * CSV Utilities for Q&A Import/Export functionality
 */

import { QnaPair } from '../types';

/**
 * Interface for CSV parsing results
 */
export interface CSVParseResult {
  success: boolean;
  data: QnaPair[];
  errors: string[];
  totalRows: number;
  validRows: number;
}


/**
 * Export Q&A pairs to CSV format
 * @param qnaPairs Array of Q&A pairs to export
 * @param filename Optional filename for the downloaded file
 */
export const exportQnaPairsToCSV = (qnaPairs: QnaPair[], filename?: string): void => {
  // Define CSV headers
  const headers = [
    'Question',
    'Answer', 
    'Tags',
    'Created Date',
    'Updated Date',
    'Imported Date',
    'Source'
  ];

  // Create CSV content with proper field escaping
  const csvContent = [
    // Header row
    headers.map(header => escapeCSVField(header)).join(','),
    // Data rows
    ...qnaPairs.map(qna => {
      const row = [
        escapeCSVField(qna.question || ''),
        escapeCSVField(qna.answer || ''),
        escapeCSVField(qna.tags ? qna.tags.join(';') : ''),
        escapeCSVField(qna.createdAt || ''),
        escapeCSVField(qna.lastUpdated || ''),
        escapeCSVField(qna.importedDate || ''),
        escapeCSVField(getSourceFromTags(qna.tags))
      ];
      return row.join(',');
    })
  ].join('\n');

  // Create and download the file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename || `qna-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

/**
 * Parse CSV file content into Q&A pairs using robust character-by-character parsing
 * Supports both header and headerless CSV files, with smart detection
 * @param csvContent String content of the CSV file
 * @param filename Optional filename to use for tagging imported pairs
 * @returns CSVParseResult with parsed data and any errors
 */
export const parseCSVToQnaPairs = (csvContent: string, filename?: string): CSVParseResult => {
  const result: CSVParseResult = {
    success: false,
    data: [],
    errors: [],
    totalRows: 0,
    validRows: 0
  };

  try {
    // Parse CSV content using robust parser that handles multi-line fields
    const rows = parseCSVContent(csvContent);

    if (rows.length === 0) {
      result.errors.push('CSV file is empty');
      return result;
    }

    // Determine if the first row contains headers or data
    const firstRow = rows[0];
    let hasHeaders = detectIfHasHeaders(firstRow);
    
    let headers: string[] = [];
    let dataRows: string[][] = [];
    let questionIndex = 0; // Default to first column
    let answerIndex = 1; // Default to second column
    let tagsIndex = -1;
    let isFromAiTrainee = false;

    if (hasHeaders) {
      headers = firstRow;
      dataRows = rows.slice(1);
      
      // Find column indices by header names
      questionIndex = findHeaderIndex(headers, ['question', 'q']);
      answerIndex = findHeaderIndex(headers, ['answer', 'a']);
      
      // If headers were detected but don't contain Q&A columns, fall back to headerless mode
      if (questionIndex === -1 || answerIndex === -1) {
        console.log(`CSV headers detected but missing Q&A columns. Headers found: [${headers.join(', ')}]. Falling back to headerless mode using first two columns.`);
        result.errors.push(`Headers detected (${headers.join(', ')}) but missing Question/Answer columns. Using first two columns instead.`);
        
        // Fall back to headerless mode
        hasHeaders = false;
        headers = [];
        dataRows = rows; // Use all rows as data
        questionIndex = 0; // First column
        answerIndex = 1; // Second column
        tagsIndex = -1;
        isFromAiTrainee = false;
      } else {
        // Headers found and contain Q&A columns - proceed normally
        // Find optional column indices
        tagsIndex = findHeaderIndex(headers, ['tags', 'tag']);
        
        // Check if this is from AI Trainee export (has date columns)
        const createdIndex = findHeaderIndex(headers, ['created date', 'created', 'createdat']);
        const updatedIndex = findHeaderIndex(headers, ['updated date', 'updated', 'lastupdated']);
        const importedIndex = findHeaderIndex(headers, ['imported date', 'imported', 'importeddate']);
        const sourceIndex = findHeaderIndex(headers, ['source']);
        
        isFromAiTrainee = createdIndex !== -1 || updatedIndex !== -1 || importedIndex !== -1 || sourceIndex !== -1;
      }
    } else {
      // No headers - treat all rows as data, use first two columns as question/answer
      dataRows = rows;
      
      // Check if any of columns 3-6 resemble dates to detect AI Trainee export
      if (dataRows.length > 0 && dataRows[0].length > 3) {
        for (let col = 2; col < Math.min(6, dataRows[0].length); col++) {
          if (dataRows[0][col] && isValidDate(dataRows[0][col])) {
            isFromAiTrainee = true;
            tagsIndex = 2; // Tags would be in column 3 (index 2)
            break;
          }
        }
      }
    }

    result.totalRows = dataRows.length;

    // Validate that we have at least 2 columns in the data
    if (dataRows.length > 0 && dataRows[0].length < 2) {
      result.errors.push('CSV must have at least 2 columns (Question and Answer)');
      return result;
    }

    for (let i = 0; i < dataRows.length; i++) {
      const rowNumber = hasHeaders ? i + 2 : i + 1; // Adjust row number based on header presence
      const fields = dataRows[i];
      
      try {
        // Ensure row has enough columns
        if (fields.length < 2) {
          result.errors.push(`Row ${rowNumber}: Must have at least 2 columns (found ${fields.length})`);
          continue;
        }

        // Extract required fields
        const question = (fields[questionIndex] || '').trim();
        const answer = (fields[answerIndex] || '').trim();
        
        if (!question) {
          result.errors.push(`Row ${rowNumber}: Question (column ${questionIndex + 1}) is required but empty`);
          continue;
        }
        
        if (!answer) {
          result.errors.push(`Row ${rowNumber}: Answer (column ${answerIndex + 1}) is required but empty`);
          continue;
        }

        // Create Q&A pair with new tagging system
        const currentDate = new Date().toISOString();
        const qnaPair: Partial<QnaPair> = {
          question,
          answer,
          createdAt: currentDate, // Set creation date to current import time
          lastUpdated: currentDate // Initially same as creation date
        };

        // Handle tagging based on source type
        if (isFromAiTrainee && tagsIndex !== -1 && fields[tagsIndex]) {
          // If from AI Trainee export, preserve existing tags
          const tagsStr = fields[tagsIndex].trim();
          if (tagsStr) {
            qnaPair.tags = tagsStr.split(';').map(tag => tag.trim()).filter(tag => tag);
          }
        } else {
          // For manual/Google Sheets CSV, use filename as tag
          if (filename) {
            const filenameWithoutExt = filename.replace(/\.[^/.]+$/, ''); // Remove extension
            qnaPair.tags = [filenameWithoutExt];
          } else {
            qnaPair.tags = ['Import'];
          }
        }

        // Don't set importedDate anymore - we use createdAt/lastUpdated instead

        result.data.push(qnaPair as QnaPair);
        result.validRows++;
        
      } catch (parseError) {
        result.errors.push(`Row ${rowNumber}: Error parsing row - ${parseError}`);
      }
    }

    result.success = result.validRows > 0;
    
    if (result.validRows === 0 && result.errors.length === 0) {
      if (hasHeaders) {
        result.errors.push(`No valid Q&A pairs found in CSV. Headers detected: [${headers.join(', ')}]. Expected Question/Answer columns or first two columns with data.`);
      } else {
        result.errors.push('No valid Q&A pairs found in CSV. Expected first two columns to contain Question and Answer data.');
      }
    }

  } catch (error) {
    result.errors.push(`Failed to parse CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  return result;
};

/**
 * Parse CSV content into a 2D array of rows and fields, properly handling multi-line quoted fields
 * @param csvContent Full CSV content as string
 * @returns Array of rows, where each row is an array of field values
 */
const parseCSVContent = (csvContent: string): string[][] => {
  const rows: string[][] = [];
  const chars = csvContent.split('');
  let currentRow: string[] = [];
  let currentField = '';
  let inQuotes = false;
  let i = 0;

  while (i < chars.length) {
    const char = chars[i];
    const nextChar = chars[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote: "" inside quoted field
        currentField += '"';
        i += 2; // Skip both quotes
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
        i++;
      }
    } else if (char === ',' && !inQuotes) {
      // Field separator (only when not in quotes)
      currentRow.push(currentField.trim());
      currentField = '';
      i++;
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      // Row separator (only when not in quotes)
      // Handle \r\n, \n, or \r line endings
      if (char === '\r' && nextChar === '\n') {
        i += 2; // Skip \r\n
      } else {
        i++; // Skip \n or \r
      }
      
      // Add the last field and finish the row
      currentRow.push(currentField.trim());
      
      // Only add non-empty rows
      if (currentRow.some(field => field.length > 0)) {
        rows.push(currentRow);
      }
      
      // Start new row
      currentRow = [];
      currentField = '';
    } else {
      // Regular character (including newlines inside quotes)
      currentField += char;
      i++;
    }
  }

  // Add the last field and row if we have content
  if (currentField.length > 0 || currentRow.length > 0) {
    currentRow.push(currentField.trim());
    if (currentRow.some(field => field.length > 0)) {
      rows.push(currentRow);
    }
  }

  return rows;
};

/**
 * Escape special characters in CSV fields and wrap in quotes when necessary
 * @param field Field value to escape
 * @returns Properly escaped and quoted field value
 */
const escapeCSVField = (field: string): string => {
  if (!field) return '';
  
  // Check if field needs to be quoted (contains comma, newline, or quote)
  const needsQuoting = field.includes(',') || field.includes('\n') || field.includes('\r') || field.includes('"');
  
  if (needsQuoting) {
    // Escape existing quotes by doubling them, then wrap in quotes
    return `"${field.replace(/"/g, '""')}"`;
  }
  
  // Field doesn't need special handling
  return field;
};

/**
 * Find header index by matching against possible header names
 * @param headers Array of header names
 * @param possibleNames Array of possible names to match (case-insensitive)
 * @returns Index of matching header, or -1 if not found
 */
const findHeaderIndex = (headers: string[], possibleNames: string[]): number => {
  const normalizedHeaders = headers.map(h => h.toLowerCase().trim());
  
  for (const name of possibleNames) {
    const index = normalizedHeaders.indexOf(name.toLowerCase());
    if (index !== -1) {
      return index;
    }
  }
  
  return -1;
};

/**
 * Determine source type from tags
 * @param tags Array of tags
 * @returns Source type string
 */
const getSourceFromTags = (tags?: string[]): string => {
  if (!tags || !Array.isArray(tags)) return '';
  
  if (tags.includes('Manual')) return 'Manual';
  if (tags.some(tag => tag.startsWith('http'))) return 'Webpage';
  if (tags.some(tag => tag.startsWith('database-'))) return 'Database';
  if (tags.some(tag => 
    !tag.includes('Manual') && 
    !tag.startsWith('http') && 
    !tag.startsWith('database-')
  )) return 'File';
  
  return 'Other';
};

/**
 * Check if a date string is valid
 * @param dateStr Date string to validate
 * @returns True if valid date, false otherwise
 */
const isValidDate = (dateStr: string): boolean => {
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
};

/**
 * Detect if the first row contains headers by analyzing field content
 * More conservative approach - only returns true if we're very confident it's headers
 * @param firstRow Array of field values from the first row
 * @returns True if row appears to contain headers, false if it contains data
 */
const detectIfHasHeaders = (firstRow: string[]): boolean => {
  if (firstRow.length < 2) return false;
  
  // Common Q&A header indicators - be more specific to Q&A contexts
  const qnaHeaders = ['question', 'q', 'answer', 'a', 'query', 'response'];
  const metaHeaders = ['tags', 'tag', 'created', 'updated', 'source', 'imported'];
  
  // Check if first few columns contain header-like text
  const firstColumn = firstRow[0]?.toLowerCase().trim() || '';
  const secondColumn = firstRow[1]?.toLowerCase().trim() || '';
  
  // Strong indicator: Contains specific Q&A header terms
  if (qnaHeaders.some(header => 
    firstColumn === header || secondColumn === header ||
    (firstColumn.includes(header) && firstColumn.length < 20) ||
    (secondColumn.includes(header) && secondColumn.length < 20)
  )) {
    return true;
  }
  
  // Medium indicator: Contains metadata headers (only if combined with other evidence)
  const hasMetaHeaders = metaHeaders.some(header => 
    firstColumn.includes(header) || secondColumn.includes(header)
  );
  
  // Only consider it headers if we have meta headers AND the columns are very short/simple
  if (hasMetaHeaders && firstColumn.length < 15 && secondColumn.length < 15 &&
      /^[a-zA-Z\s]+$/.test(firstColumn) && /^[a-zA-Z\s]+$/.test(secondColumn)) {
    return true;
  }
  
  // Conservative check: Both columns are very short, single words, and contain no punctuation
  // This catches cases like "Code, Description" but avoids actual data
  if (firstColumn.length < 12 && secondColumn.length < 12 &&
      /^[a-zA-Z]+\s*[a-zA-Z]*$/.test(firstColumn) && 
      /^[a-zA-Z]+\s*[a-zA-Z]*$/.test(secondColumn) &&
      !firstColumn.includes('?') && !secondColumn.includes('?') &&
      !firstColumn.includes('.') && !secondColumn.includes('.') &&
      !(/\d/.test(firstColumn)) && !(/\d/.test(secondColumn))) {
    return true;
  }
  
  return false;
};

/**
 * Generate a preview of CSV data for user confirmation
 * @param parseResult Result from CSV parsing
 * @param maxPreviewRows Maximum number of rows to show in preview
 * @returns Preview object with summary and sample data
 */
export const generateCSVPreview = (parseResult: CSVParseResult, maxPreviewRows: number = 5) => {
  return {
    summary: {
      totalRows: parseResult.totalRows,
      validRows: parseResult.validRows,
      errorCount: parseResult.errors.length,
      success: parseResult.success
    },
    sampleData: parseResult.data.slice(0, maxPreviewRows),
    errors: parseResult.errors.slice(0, 10), // Show max 10 errors
    hasMore: {
      data: parseResult.data.length > maxPreviewRows,
      errors: parseResult.errors.length > 10
    }
  };
};